import React from "react";
import { displayValue, isEmptyValue } from "utilities/displayValue";

/**
 * Shared label/value row. Used in 54 places across MID Detail, Company
 * Overview, DBA rows, MID applications and the registration tab.
 *
 * SOUND-643 fixed two things here:
 *
 * 1. It rendered "Not Provided" where the schema-driven record view renders an
 *    em-dash — the two sat inches apart on the registration tab.
 *
 * 2. It was `value ? value : "Not Provided"`, so `0` and `false` were reported
 *    as missing. A merchant who answered "no" to a risk question, or 0% mail
 *    order, read as having told us nothing. Emptiness now comes from
 *    utilities/displayValue, which treats false and 0 as real answers.
 *
 * Empty values are also dimmed rather than rendered at full weight, so a blank
 * row reads as "nothing on record" at a glance instead of as content.
 */
export function InfoField({ label, value, className = "" }) {
  // Five call sites pass a rendered element rather than a scalar — StatusBadge
  // and SourceBadge in MidDetail, MidCreate and MidApplication. Those must be
  // rendered as-is: handing a React element to displayValue() sends it down the
  // `typeof value === "object"` branch into JSON.stringify, which throws on the
  // circular fiber tree ("Converting circular structure to JSON") and takes the
  // whole page down. An element is also never "empty".
  const isElement = React.isValidElement(value);
  const empty = !isElement && isEmptyValue(value);

  return (
    <div className={className}>
      <dt className="text-sm font-medium text-foreground mb-1">{label}</dt>
      <dd
        className={
          empty
            ? "text-sm text-muted-foreground/50"
            : "text-sm text-muted-foreground break-words"
        }
        title={empty ? "No value on record" : undefined}
      >
        {isElement ? value : displayValue(value)}
      </dd>
    </div>
  );
}
